import { Fragment } from "react";
import { Grid, GridItem } from "@chakra-ui/react";

/**
 * Custom HeatMapGrid component - drop-in replacement for react-grid-heatmap
 * Handles both dense arrays (data.length === yLabels.length) and sparse arrays (data has offset)
 */
const HeatMapGrid = ({
  data = [],
  xLabels = [],
  yLabels = [],
  cellRender,
  cellStyle,
  xLabelsStyle,
  yLabelsStyle,
  cellHeight = "2rem",
  xLabelsPos = "bottom",
  yLabelsPos = "left",
}) => {
  // Determine if data is dense (same length as yLabels) or sparse (has offset/empty rows)
  const isDense = data.length === yLabels.length;

  // For sparse arrays, find offset by locating first non-empty row
  let offset = 0;
  if (!isDense) {
    offset = data.findIndex(row => row && row.length > 0);
    if (offset < 0) offset = 0;
  }

  // Build rows to render: each yLabel maps to corresponding data row
  const rowsToRender = yLabels.map((label, labelIndex) => {
    const dataIndex = isDense ? labelIndex : labelIndex + offset;
    const row = data[dataIndex] || [];
    return { label, row, dataIndex };
  });

  // Calculate max value for ratio calculation
  const flatData = rowsToRender.flatMap(item => item.row);
  const maxValue = Math.max(...flatData, 1); // Avoid division by zero

  const numCols = xLabels.length;
  const numRows = rowsToRender.length;
  const showYLabelsLeft = yLabelsPos === "left";
  const showXLabelsTop = xLabelsPos === "top";

  // Grid template columns: y-label column + data columns
  const gridTemplateColumns = showYLabelsLeft
    ? `auto repeat(${numCols}, 1fr)`
    : `repeat(${numCols}, 1fr) auto`;

  // Grid template rows: x-label row (if top) + data rows + x-label row (if bottom)
  const gridTemplateRows = showXLabelsTop
    ? `auto repeat(${numRows}, ${cellHeight})`
    : `repeat(${numRows}, ${cellHeight}) auto`;

  return (
    <Grid
      templateColumns={gridTemplateColumns}
      templateRows={gridTemplateRows}
      gap="1px"
    >
      {/* X Labels - Top */}
      {showXLabelsTop && (
        <>
          {showYLabelsLeft && <GridItem />}
          {xLabels.map((label, index) => (
            <GridItem
              key={`x-top-${index}`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={xLabelsStyle ? xLabelsStyle(index) : {}}
            >
              {label}
            </GridItem>
          ))}
          {!showYLabelsLeft && <GridItem />}
        </>
      )}

      {/* Data rows with Y labels */}
      {rowsToRender.map(({ row, label, dataIndex }, displayIndex) => (
        <Fragment key={`row-${dataIndex}`}>
          {/* Y Label - Left */}
          {showYLabelsLeft && (
            <GridItem
              key={`y-label-${dataIndex}`}
              display="flex"
              alignItems="flex-start"
              justifyContent="flex-end"
              pr="8px"
              style={yLabelsStyle ? yLabelsStyle(displayIndex) : {}}
            >
              {label}
            </GridItem>
          )}

          {/* Data cells */}
          {row.length > 0 ? row.map((value, colIndex) => {
            const ratio = maxValue > 0 ? value / maxValue : 0;
            const style = cellStyle ? cellStyle(dataIndex, colIndex, ratio) : {};

            return (
              <GridItem
                key={`cell-${dataIndex}-${colIndex}`}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="4px"
                style={style}
              >
                {cellRender ? cellRender(dataIndex, colIndex, value) : value}
              </GridItem>
            );
          }) : xLabels.map((_, colIndex) => (
            <GridItem
              key={`cell-${dataIndex}-${colIndex}`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="4px"
              style={cellStyle ? cellStyle(dataIndex, colIndex, 0) : {}}
            >
              {cellRender ? cellRender(dataIndex, colIndex, 0) : 0}
            </GridItem>
          ))}

          {/* Y Label - Right */}
          {!showYLabelsLeft && (
            <GridItem
              key={`y-label-${dataIndex}`}
              display="flex"
              alignItems="flex-start"
              pl="8px"
              style={yLabelsStyle ? yLabelsStyle(displayIndex) : {}}
            >
              {label}
            </GridItem>
          )}
        </Fragment>
      ))}

      {/* X Labels - Bottom */}
      {!showXLabelsTop && (
        <>
          {showYLabelsLeft && <GridItem />}
          {xLabels.map((label, index) => (
            <GridItem
              key={`x-bottom-${index}`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={xLabelsStyle ? xLabelsStyle(index) : {}}
            >
              {label}
            </GridItem>
          ))}
          {!showYLabelsLeft && <GridItem />}
        </>
      )}
    </Grid>
  );
};

export default HeatMapGrid;
